import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { GlowCard } from '../components/GlowCard.js';

export default function SignUpPage() {
  const { signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error: err } = await signUp(email, password);
    setLoading(false);

    if (err) {
      setError(err);
    } else {
      setDone(true);
    }
  }

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pix-void px-4">
        <div className="w-full max-w-sm text-center">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-pix-pink/10 mx-auto">
            <svg className="h-8 w-8 text-pix-pink" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-white">Check your email</h1>
          <p className="text-sm text-white/40">
            We sent a confirmation link to <span className="text-white">{email}</span>. Click it to activate your account.
          </p>
          <Link to="/login" className="mt-6 inline-block text-sm font-medium text-pix-pink hover:text-pix-pink-dark transition-colors">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-pix-void px-4">
      <div className="w-full max-w-sm">
        {/* Branding */}
        <div className="mb-8 text-center">
          <div className="mb-2 flex items-center justify-center gap-2 text-xs text-white/30">
            <span className="font-bold tracking-widest uppercase">PIX3L</span>
            <span>/</span>
            <span>Cascade</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Create your account</h1>
          <p className="mt-1 text-sm text-white/40">Start automating your content pipeline.</p>
        </div>

        <GlowCard className="rounded-2xl border border-white/5 bg-pix-deep p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-xs font-medium uppercase tracking-wider text-white/40">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 block w-full rounded-lg border border-white/10 bg-pix-void px-3 py-2.5 text-sm text-white placeholder-white/20 focus:border-pix-pink/50 focus:outline-none focus:ring-1 focus:ring-pix-pink/50"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium uppercase tracking-wider text-white/40">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 block w-full rounded-lg border border-white/10 bg-pix-void px-3 py-2.5 text-sm text-white placeholder-white/20 focus:border-pix-pink/50 focus:outline-none focus:ring-1 focus:ring-pix-pink/50"
              />
            </div>

            <div>
              <label htmlFor="confirm" className="block text-xs font-medium uppercase tracking-wider text-white/40">
                Confirm password
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 block w-full rounded-lg border border-white/10 bg-pix-void px-3 py-2.5 text-sm text-white placeholder-white/20 focus:border-pix-pink/50 focus:outline-none focus:ring-1 focus:ring-pix-pink/50"
              />
            </div>

            {error && (
              <p className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-pix-pink px-4 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_rgba(233,30,140,0.2)] hover:bg-pix-pink-dark disabled:opacity-50 transition-colors"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        </GlowCard>

        <p className="mt-6 text-center text-sm text-white/30">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-pix-pink hover:text-pix-pink-dark transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
